/**
 * TaskCreateForm
 *
 * Right-panel form for creating a new task. Mirrors TaskDetail's visual
 * structure and FIELD ORDER exactly — both surfaces share field primitives
 * from `ui/taskFields.tsx` so the create + edit experience stay aligned.
 */

import { useState, useRef, useEffect } from 'react';
import { X, Loader2, Check, Plus, Mic } from 'lucide-react';
import clsx from 'clsx';
import { createTask } from '../api/client';
import { createProject } from '../api/projects';
import { useProjects } from '../hooks/useProjects';
import { useLabels } from '../hooks/useLabels';
import { btn } from './ui/buttonStyles';
import { DatePicker } from './DatePicker';
import {
  inputClasses,
  TaskMetadataGrid,
  FieldRow,
  FieldSelect,
} from './ui/taskFields';
import type { Task } from '../types';

const priorityColors: Record<number, string> = {
  1: 'text-priority-1 bg-red-50 dark:bg-red-950/50',
  2: 'text-priority-2 bg-orange-50 dark:bg-orange-950/50',
  3: 'text-priority-3 bg-yellow-50 dark:bg-yellow-950/50',
  4: 'text-text-secondary-light bg-surface-base-light dark:text-text-secondary-dark dark:bg-surface-raised-dark/50',
};

const priorityBorderColors: Record<number, string> = {
  1: 'border-priority-1 hover:bg-red-50 dark:hover:bg-red-900/20',
  2: 'border-priority-2 hover:bg-orange-50 dark:hover:bg-orange-900/20',
  3: 'border-priority-3 hover:bg-yellow-50 dark:hover:bg-yellow-900/20',
  4: 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800',
};

const priorityCheckColors: Record<number, string> = {
  1: 'text-priority-1',
  2: 'text-priority-2',
  3: 'text-priority-3',
  4: 'text-gray-400 dark:text-gray-400',
};

const priorityOptions = [
  { value: 1, label: 'Urgent' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'Low' },
];

const TIME_ESTIMATE_OPTIONS = [
  { value: '15-minutes', label: '15 minutes' },
  { value: '30-minutes', label: '30 minutes' },
  { value: '45-minutes', label: '45 minutes' },
  { value: '1-hour', label: '1 hour' },
  { value: '90-minutes', label: '90 minutes' },
  { value: '2-hours', label: '2 hours' },
  { value: 'needs-breakdown', label: 'Needs breakdown' },
];

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekday', label: 'Every weekday' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

const NEW_PROJECT_VALUE = '__new__';

interface TaskCreateFormProps {
  onCreated: (task: Task) => void;
  onCancel: () => void;
  /** Called when the user picks "Brain dump" mode (opens the recording overlay). */
  onBrainDump?: () => void;
}

export function TaskCreateForm({ onCreated, onCancel, onBrainDump }: TaskCreateFormProps) {
  const { projects, refetch: refetchProjects } = useProjects();
  const { labelNames: existingLabels } = useLabels();


  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<number>(4);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [projectId, setProjectId] = useState('');
  const [timeEstimate, setTimeEstimate] = useState('');
  const [recurrenceRule, setRecurrenceRule] = useState('');
  const [labels, setLabels] = useState<string[]>([]);

  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  const [labelInput, setLabelInput] = useState('');
  const [showLabelSuggestions, setShowLabelSuggestions] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const labelSuggestions = labelInput.trim()
    ? existingLabels
        .filter((l) => l.toLowerCase().includes(labelInput.toLowerCase()) && !labels.includes(l))
        .slice(0, 6)
    : [];

  const addLabel = (label: string) => {
    const trimmed = label.trim();
    if (!trimmed || labels.includes(trimmed)) {
      setLabelInput('');
      return;
    }
    setLabels([...labels, trimmed]);
    setLabelInput('');
  };

  const removeLabel = (label: string) => {
    setLabels(labels.filter((l) => l !== label));
  };

  const handleLabelKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (labelInput.trim()) addLabel(labelInput);
    } else if (e.key === 'Backspace' && !labelInput && labels.length > 0) {
      setLabels(labels.slice(0, -1));
    } else if (e.key === ',') {
      e.preventDefault();
      if (labelInput.trim()) addLabel(labelInput);
    }
  };

  const handleCreateProject = async () => {
    const name = newProjectName.trim();
    if (!name || isCreatingProject) return;
    setIsCreatingProject(true);
    try {
      const project = await createProject({ name });
      await refetchProjects();
      setProjectId(project.id);
      setShowNewProject(false);
      setNewProjectName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleProjectChange = (value: string) => {
    if (value === NEW_PROJECT_VALUE) {
      setShowNewProject(true);
      return;
    }
    setProjectId(value);
    setShowNewProject(false);
  };

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const allLabels = timeEstimate ? [...labels, timeEstimate] : labels;
      const task = await createTask({
        content: content.trim(),
        description: description.trim() || undefined,
        priority,
        dueDate: dueDate || undefined,
        dueTime: dueTime || undefined,
        projectId: projectId || undefined,
        labels: allLabels.length > 0 ? allLabels : undefined,
        recurrenceRule: recurrenceRule || undefined,
      });
      onCreated(task);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create task');
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  return (
    <div
      className="h-full flex flex-col bg-surface-raised-light dark:bg-surface-base-dark relative"
      onKeyDown={handleKeyDown}
    >
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-24 space-y-6">
        {/* Title row */}
        <div className="flex items-start gap-3">
          <div
            className={clsx(
              'shrink-0 w-7 h-7 md:w-6 md:h-6 rounded-sm border-2 flex items-center justify-center mt-1.5 group',
              priorityBorderColors[priority],
            )}
            aria-hidden="true"
          >
            <Check
              className={clsx('w-3 h-3 opacity-0 group-hover:opacity-30 transition-opacity', priorityCheckColors[priority])}
              strokeWidth={3}
            />
          </div>
          <div className="flex-1 min-w-0">
            <textarea
              ref={titleRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="New task"
              rows={1}
              className={clsx(
                'w-full text-lg font-medium text-text-primary-light dark:text-text-primary-dark',
                'bg-surface-raised-light dark:bg-surface-raised-dark px-2 py-1 -mx-2',
                'border border-primary-300 dark:border-primary-600 rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-primary-500',
                'resize-none overflow-hidden placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark',
              )}
            />
          </div>
          <button
            onClick={onCancel}
            className="shrink-0 p-2.5 md:p-1.5 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark transition-colors text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark cursor-pointer mt-0.5"
            aria-label="Cancel"
            title="Cancel (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Description — same order as TaskDetail */}
        <FieldRow label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add notes, links, context…"
            rows={5}
            className={clsx(inputClasses, 'resize-y leading-relaxed')}
          />
        </FieldRow>

        {/* Metadata grid — same order as TaskDetail. 6 fields → no widow */}
        <TaskMetadataGrid>
          <FieldRow label="Due Date">
            <DatePicker
              value={dueDate}
              onChange={setDueDate}
              timeValue={dueTime}
              onTimeChange={setDueTime}
            />
          </FieldRow>

          <FieldRow label="Priority">
            <FieldSelect
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className={priorityColors[priority]}
            >
              {priorityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {`P${opt.value} — ${opt.label}`}
                </option>
              ))}
            </FieldSelect>
          </FieldRow>

          <FieldRow label="Project">
            {showNewProject ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  autoFocus
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void handleCreateProject(); }
                    else if (e.key === 'Escape') { e.preventDefault(); setShowNewProject(false); setNewProjectName(''); }
                  }}
                  placeholder="New project name"
                  className={inputClasses}
                />
                <button
                  type="button"
                  onClick={handleCreateProject}
                  disabled={!newProjectName.trim() || isCreatingProject}
                  className={clsx(btn.primary.sm, 'shrink-0')}
                >
                  {isCreatingProject ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowNewProject(false); setNewProjectName(''); }}
                  className="shrink-0 p-2 rounded-lg text-text-tertiary-light dark:text-text-tertiary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark cursor-pointer"
                  aria-label="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <FieldSelect value={projectId} onChange={(e) => handleProjectChange(e.target.value)}>
                <option value="">No Project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
                <option value={NEW_PROJECT_VALUE}>+ Create new project…</option>
              </FieldSelect>
            )}
          </FieldRow>

          <FieldRow label="Estimate">
            <FieldSelect value={timeEstimate} onChange={(e) => setTimeEstimate(e.target.value)}>
              <option value="">No estimate</option>
              {TIME_ESTIMATE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </FieldSelect>
          </FieldRow>

          {/* Repeat spans 2 cols to avoid a widow row */}
          <FieldRow label="Repeat" full>
            <FieldSelect value={recurrenceRule} onChange={(e) => setRecurrenceRule(e.target.value)}>
              <option value="">Does not repeat</option>
              {RECURRENCE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </FieldSelect>
          </FieldRow>
        </TaskMetadataGrid>

        {/* Labels */}
        <FieldRow label="Labels">
          <div className="relative">
            <div
              className={clsx(inputClasses, 'flex flex-wrap items-center gap-1.5 min-h-[44px] cursor-text')}
              onClick={(e) => {
                const input = e.currentTarget.querySelector('input');
                input?.focus();
              }}
            >
              {labels.map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[11px] font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300"
                >
                  {label}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeLabel(label); }}
                    className="hover:text-primary-900 dark:hover:text-primary-100 cursor-pointer"
                    aria-label={`Remove ${label}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                onKeyDown={handleLabelKeyDown}
                onFocus={() => setShowLabelSuggestions(true)}
                onBlur={() => setTimeout(() => setShowLabelSuggestions(false), 150)}
                placeholder={labels.length === 0 ? 'Type a label, press Enter…' : ''}
                className="flex-1 min-w-[80px] bg-transparent outline-none text-sm placeholder:text-text-tertiary-light dark:placeholder:text-text-tertiary-dark"
              />
            </div>
            {showLabelSuggestions && labelSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg shadow-lg overflow-hidden">
                {labelSuggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); addLabel(suggestion); }}
                    className="w-full text-left px-3 py-2 text-sm font-mono hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark cursor-pointer text-text-primary-light dark:text-text-primary-dark"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
        </FieldRow>

        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {onBrainDump && (
          <button
            type="button"
            onClick={onBrainDump}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-dashed border-border-subtle-light dark:border-border-subtle-dark hover:border-primary-500 dark:hover:border-primary-400 hover:bg-primary-50/50 dark:hover:bg-primary-900/10 transition-colors text-left cursor-pointer group"
          >
            <Mic className="w-5 h-5 text-primary-500 dark:text-primary-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary-light dark:text-text-primary-dark">
                Brain dump multiple tasks
              </p>
              <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark mt-0.5">
                Speak and let Danny extract a list
              </p>
            </div>
          </button>
        )}

        <p className="text-xs font-mono text-text-tertiary-light dark:text-text-tertiary-dark">
          Press <kbd className="px-1.5 py-0.5 rounded bg-surface-base-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark">⌘ Enter</kbd> to add · <kbd className="px-1.5 py-0.5 rounded bg-surface-base-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark">Esc</kbd> to cancel
        </p>
      </div>

      <button
        onClick={handleSubmit}
        disabled={isSubmitting || !content.trim()}
        className={clsx(btn.primary.md, 'absolute bottom-4 right-4 shadow-lg')}
      >
        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={2.5} />}
        Add Task
      </button>
    </div>
  );
}
